import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  reload,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, MailCheck } from "lucide-react";

const googleProvider = new GoogleAuthProvider();

// Friendly copy for the common Firebase error codes.
function friendly(code) {
  const map = {
    "auth/invalid-email": "That email address doesn't look right.",
    "auth/user-not-found": "No account found with that email.",
    "auth/wrong-password": "Incorrect email or password.",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/email-already-in-use": "An account with that email already exists. Try signing in.",
    "auth/weak-password": "Please choose a password with at least 6 characters.",
    "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
    "auth/popup-closed-by-user": "Sign-in was cancelled.",
    "auth/popup-blocked": "Your browser blocked the popup, retrying with a redirect…",
  };
  return map[code] || "Something went wrong. Please try again.";
}

export default function FirebaseAuthPanel() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | verify | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState("");

  // Exchange a verified Firebase user for our own session cookie, then enter the app.
  const enterApp = async (fbUser) => {
    const idToken = await fbUser.getIdToken(/* forceRefresh */ true);
    const res = await api.post("/auth/firebase-session", { id_token: idToken });
    setUser(res.data);
    navigate("/dashboard", { replace: true });
  };

  // Handle Google redirect fallback result on mount.
  useEffect(() => {
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          setBusy("google");
          await enterApp(result.user);
        }
      } catch (e) {
        if (e?.code) toast.error(friendly(e.code));
      } finally {
        setBusy("");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleSignIn = async () => {
    setBusy("google");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await enterApp(result.user);
    } catch (e) {
      if (e?.code === "auth/popup-blocked" || e?.code === "auth/cancelled-popup-request") {
        try {
          await signInWithRedirect(auth, googleProvider);
          return;
        } catch (_) {
          toast.error("Could not open Google sign-in.");
        }
      } else if (e?.code !== "auth/popup-closed-by-user") {
        toast.error(friendly(e?.code));
      }
      setBusy("");
    }
  };

  const emailSignIn = async () => {
    setBusy("email");
    try {
      const { user } = await signInWithEmailAndPassword(auth, email.trim(), password);
      await reload(user);
      if (!user.emailVerified) {
        setMode("verify");
        toast.message("Please verify your email to continue.");
        setBusy("");
        return;
      }
      await enterApp(user);
    } catch (e) {
      toast.error(friendly(e?.code));
      setBusy("");
    }
  };

  const emailSignUp = async () => {
    if (password.length < 6) {
      toast.error("Please choose a password with at least 6 characters.");
      return;
    }
    setBusy("email");
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await sendEmailVerification(user);
      setMode("verify");
      toast.success("Account created. Check your inbox to verify your email.");
    } catch (e) {
      toast.error(friendly(e?.code));
    } finally {
      setBusy("");
    }
  };

  const resendVerification = async () => {
    setBusy("resend");
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        toast.success("Verification email sent again.");
      }
    } catch (e) {
      toast.error(friendly(e?.code));
    } finally {
      setBusy("");
    }
  };

  const checkedVerified = async () => {
    setBusy("check");
    try {
      if (!auth.currentUser) {
        setMode("signin");
        return;
      }
      await reload(auth.currentUser);
      if (auth.currentUser.emailVerified) {
        await enterApp(auth.currentUser);
      } else {
        toast.error("Not verified yet. Please click the link in your email.");
      }
    } catch (e) {
      toast.error(friendly(e?.code));
    } finally {
      setBusy("");
    }
  };

  const sendReset = async () => {
    if (!email.trim()) {
      toast.error("Enter your email first.");
      return;
    }
    setBusy("reset");
    try {
      await sendPasswordResetEmail(auth, email.trim());
      toast.success("Password reset email sent.");
      setMode("signin");
    } catch (e) {
      toast.error(friendly(e?.code));
    } finally {
      setBusy("");
    }
  };

  if (mode === "verify") {
    return (
      <div className="w-full max-w-sm animate-gentle-fade" data-testid="firebase-verify-panel">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <MailCheck className="h-6 w-6 text-primary" />
        </div>
        <h2 className="font-heading text-3xl text-foreground mb-2">Verify your email</h2>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          We sent a verification link to <span className="font-medium text-foreground">{email || "your inbox"}</span>.
          Click it, then come back and continue.
        </p>
        <div className="space-y-3">
          <Button onClick={checkedVerified} disabled={busy === "check"} data-testid="verify-continue-button"
            className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground">
            {busy === "check" ? <Loader2 className="h-4 w-4 animate-spin" /> : "I've verified, continue"}
          </Button>
          <Button onClick={resendVerification} disabled={busy === "resend"} variant="outline"
            data-testid="verify-resend-button" className="w-full rounded-full h-11 border-border gap-2">
            <Mail className="h-4 w-4" /> Resend email
          </Button>
          <button onClick={() => setMode("signin")} className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 pt-2"
            data-testid="verify-back-button">
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (mode === "reset") {
    return (
      <div className="w-full max-w-sm animate-gentle-fade" data-testid="firebase-reset-panel">
        <h2 className="font-heading text-3xl text-foreground mb-2">Reset password</h2>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          Enter your email and we'll send you a reset link.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              placeholder="you@example.com" className="rounded-xl h-11" data-testid="reset-email-input" />
          </div>
          <Button onClick={sendReset} disabled={busy === "reset"} data-testid="reset-send-button"
            className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground">
            {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
          </Button>
          <button onClick={() => setMode("signin")} className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 pt-2"
            data-testid="reset-back-button">
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  const signingUp = mode === "signup";
  return (
    <div className="w-full max-w-sm animate-gentle-fade" data-testid="firebase-auth-panel">
      <h2 className="font-heading text-3xl text-foreground mb-2">
        {signingUp ? "Create your workspace" : "Welcome"}
      </h2>
      <p className="text-muted-foreground mb-8 leading-relaxed">
        {signingUp
          ? "Sign up to open your private business command center."
          : "Sign in to your private business command center."}
      </p>

      <Button onClick={googleSignIn} disabled={busy === "google"} data-testid="google-login-button"
        variant="outline" className="w-full rounded-full h-12 border-border gap-2 mb-5">
        {busy === "google" ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
        )}
        Continue with Google
      </Button>

      <div className="flex items-center gap-3 mb-5">
        <div className="h-px bg-border flex-1" />
        <span className="text-xs text-muted-foreground">or</span>
        <div className="h-px bg-border flex-1" />
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
            placeholder="you@example.com" className="rounded-xl h-11" data-testid="email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Password</Label>
            {!signingUp && (
              <button onClick={() => setMode("reset")} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                data-testid="forgot-password-button">
                Forgot?
              </button>
            )}
          </div>
          <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password"
            placeholder="••••••••" className="rounded-xl h-11" data-testid="password-input"
            onKeyDown={(e) => { if (e.key === "Enter") (signingUp ? emailSignUp() : emailSignIn()); }} />
        </div>
        <Button onClick={signingUp ? emailSignUp : emailSignIn} disabled={busy === "email"}
          data-testid={signingUp ? "email-signup-button" : "email-signin-button"}
          className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground">
          {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : (signingUp ? "Create account" : "Sign in")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mt-6 text-center">
        {signingUp ? "Already have an account?" : "New here?"}{" "}
        <button onClick={() => setMode(signingUp ? "signin" : "signup")}
          className="text-primary font-medium hover:underline underline-offset-2"
          data-testid="toggle-auth-mode-button">
          {signingUp ? "Sign in" : "Create one"}
        </button>
      </p>
    </div>
  );
}
